import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Actions
import { closeCampaignGraphReportOtherCitiesInfoForm } from "../../../actions/graph-reports/CampaignGraphReportActions";

// Constants and Utils
import { formatText } from "../../../common-utils/string-utils/StringUtils";
import { downloadPdf } from "../../../common-utils/file-utils/PdfUtil";
import { FormDataTargets } from "../../../constants/GeneralConstants";
import { MetricsOptionsDropdownItems } from "../GraphReportsConstants";

// Components
import TableHeaders from "../../../components/table/TableHeaders";
import Spinner from "../../../components/spinner/Spinner";

// page Constants
const graphReportOtherCitiesBodyId = "graph-report-other-cities";
const tableHeaders = [
  {
    title: {
      displayName: "Name",
      className: "col-4",
    },
  },
  {
    title: {
      displayName: "Percentage",
      className: "col-4 text-center",
    },
  },
  {
    title: {
      displayName: "Count",
      className: "col-4 text-center",
    },
  },
];

/**
 * Campaign Graph Report Other Cities Info Form
 */
export default function CampaignGraphReportOtherCitiesInfoForm() {
  const dispatch = useDispatch();

  // State
  const [selectedMetricLabel, setSelectedMetricLabel] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchedCities, setSearchedCities] = useState([]);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);

  // Selector
  const graphReportOtherCitiesInfoProcessDataObj = useSelector(
    (state) =>
      state.campaignGraphReport.graphReportOtherCitiesInfoProcessDataObj
  );
  const { selectedMetric, remainingDataMap, campaignTitle } =
    graphReportOtherCitiesInfoProcessDataObj || {};

  // other cities data
  const otherCitiesData = remainingDataMap[selectedMetric];

  // update selected label
  useEffect(() => {
    const item = MetricsOptionsDropdownItems.find(
      (option) => option.id === selectedMetric
    );
    const metricLabel = item?.label || "";

    // update state
    setSelectedMetricLabel(metricLabel);
  }, [selectedMetric]);

  // update searchedCities with initialCities
  useEffect(() => {
    if (!searchText) {
      setSearchedCities(otherCitiesData);
    } else {
      // filter searched cities
      const filteredCities = otherCitiesData.filter((city) =>
        city.name.toLowerCase().includes(searchText.toLowerCase())
      );

      // update cities
      setSearchedCities(filteredCities);
    }
  }, [searchText]);

  // download pdf
  async function downloadOtherCitiesInfo() {
    const fileName = `${campaignTitle}_City Metrics Split For Other Cities.pdf`;
    const input = document.getElementById(graphReportOtherCitiesBodyId);
    setIsPdfDownloading(true);

    try {
      await downloadPdf(input, fileName);

      // toaster
      toast.success("Successfully Downloaded");
    } catch (err) {
      toast.error("Download failed");
      console.error("Download failed:", err);
    } finally {
      setIsPdfDownloading(false);
    }
  }

  // check cities present
  const isCitiesPresent = searchedCities.length > 0;

  return (
    <div
      className="modal fade"
      id={FormDataTargets.campaignGraphReportOtherCitiesInfoForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          {/* Form Header */}
          <div className="modal-header bg-alt px-5 align-items-baseline">
            <h4 className="modal-title">{"Other Cities Metrics"}</h4>
            <div className="d-flex align-items-center">
              <button
                className="btn text-primary shadow-none mr-2"
                onClick={downloadOtherCitiesInfo}
                disabled={isPdfDownloading}
              >
                {isPdfDownloading && (
                  <Spinner className="spinner-border-sm mr-1" />
                )}
                Download PDF
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-3 rounded-lg shadow-none"
                data-dismiss="modal"
                onClick={() =>
                  dispatch(closeCampaignGraphReportOtherCitiesInfoForm())
                }
              >
                {"X"}
              </button>
            </div>
          </div>

          {/* Form body */}
          <div
            className="modal-body px-5 py-4"
            id={graphReportOtherCitiesBodyId}
          >
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                Other City Metrics Split For{" "}
                <span className="text-primary border p-1 rounded-lg">
                  {selectedMetricLabel}
                </span>
              </h5>

              <input
                type="search"
                className="form-control shadow-none rounded-lg col-3"
                value={searchText}
                placeholder={"Search City"}
                onChange={({ target }) => setSearchText(target.value)}
              />
            </div>

            <div className="table-responsive mt-3">
              <table className="table">
                <TableHeaders
                  tableHeaders={tableHeaders}
                  requiredSubHeader={false}
                />
                <tbody>
                  {!isCitiesPresent && (
                    <tr className="text-center">
                      <td colSpan={3} className="font-italic text-muted py-4">
                        No City Found
                      </td>
                    </tr>
                  )}

                  {isCitiesPresent &&
                    searchedCities.map((city, i) => {
                      const { name, value, count } = city;
                      return (
                        <tr key={i}>
                          <td>{name}</td>
                          <td className="text-center">{formatText(value)}</td>
                          <td className="text-center">{formatText(count)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
